import React, { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import les_top from '../../assets/les-top.svg';
import '../../scss/components/LessonsPage/LessonCard.scss';
import { updateLessonAccess } from '../../redux/slices/lessonAccessSlice';

const LessonCard = memo(({ lesson, moduleId, index, totalLessons, isLocked = false, lockReason = '' }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id, title, description } = lesson;
    const lessonIndex = index;
    const totalLessonsCount = totalLessons;
    const adminMode = useSelector((state) => state.adminMode.adminMode);
    const { lessonAccess } = useSelector(state => state.lessonAccess);
    const [currentLessonAccess, setCurrentLessonAccess] = useState(null);

    // Получаем информацию о доступе к текущему уроку
    useEffect(() => {
        if (lessonAccess && lessonAccess.length > 0) {
            const moduleIdNum = Number(moduleId);
            const lessonIdNum = Number(id);

            const accessInfo = lessonAccess.find(
                access => access.moduleId === moduleIdNum && access.lessonId === lessonIdNum
            );

            if (accessInfo) {
                setCurrentLessonAccess(accessInfo);
            }
        }
    }, [lessonAccess, moduleId, id]);

    // Функция для переключения статуса блокировки урока (только для админов)
    const toggleLessonLock = async (e) => {
        e.stopPropagation();
        if (!currentLessonAccess || !currentLessonAccess.canModify) return;

        try {
            const moduleIdNum = Number(moduleId);
            const lessonIdNum = Number(id);
            const newLockedStatus = !currentLessonAccess.isLocked;

            await dispatch(updateLessonAccess({
                moduleId: moduleIdNum,
                lessonId: lessonIdNum,
                isLocked: newLockedStatus
            }));

            // Обновляем локальное состояние
            setCurrentLessonAccess({
                ...currentLessonAccess,
                isLocked: newLockedStatus
            });
        } catch (error) {
            console.error('Ошибка при изменении статуса доступа к уроку:', error);
        }
    };

    // Обработчик клика по карточке урока
    const handleStartLesson = () => {
        // Администраторам доступны все уроки
        if (adminMode) {
            navigate(`/lessons/${moduleId}/${id}`, {
                state: {
                    lessonAccess: {
                        isLocked: false,
                        lockReason: '',
                        canAccess: true
                    }
                }
            });
            return;
        }

        // Всегда проверяем доступ, даже если кнопка disabled
        if (isLocked) {
            // Показываем сообщение о причине блокировки
            return;
        }

        // Дополнительная проверка через Redux состояние
        const moduleIdNum = Number(moduleId);
        const lessonIdNum = Number(id);

        // Проверяем доступ через lessonAccess из Redux
        const accessInfo = lessonAccess.find(
            access => access.moduleId === moduleIdNum && access.lessonId === lessonIdNum
        );

        if (accessInfo && accessInfo.isLocked) {
            return;
        }

        // Если все проверки пройдены, переходим к уроку с информацией о доступе
        navigate(`/lessons/${moduleId}/${id}`, {
            state: {
                lessonAccess: {
                    isLocked: isLocked,
                    lockReason: lockReason,
                    canAccess: !isLocked && (!accessInfo || !accessInfo.isLocked)
                }
            }
        });
    };

    // Функция для определения текста блокировки
    const getLockMessage = () => {
        switch (lockReason) {
            case 'acceptance':
                return 'ПО ПОДПИСКЕ';
            case 'completion':
                return 'ПРОЙДИ ПРОШЛЫЙ УРОК';
            case 'locked':
                return 'СКОРО ОТКРОЕТСЯ';
            default:
                return 'ЗАБЛОКИРОВАНО';
        }
    };

    // Определяем текст кнопки в зависимости от причины блокировки
    const getButtonText = () => {
        if (!isLocked) {
            return 'ВЫПОЛНИТЬ УРОК';
        }
        return getLockMessage();
    };

    return (
        <div className={`lesson-card ${isLocked && !adminMode ? 'lesson-locked' : ''}`}>
            <img src={les_top} alt="Иконка урока" className="les-top-icon" />
            <div className="lesson-number">
                <span className="current-number">{lessonIndex}</span>
                <span className="current-number">/</span>
                <span className="total-number">{totalLessonsCount}</span>
            </div>

            <div className="lesson-background">
                <div className="background-rect"></div>
                <svg className="corner top-left" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M34 2H13.6364L2 12.2655V31" stroke="#E9F7FA" strokeWidth="3" strokeMiterlimit="10" />
                </svg>
                <svg className="corner top-right" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 2H36.3636L48 12.2655V31" stroke="#E9F7FA" strokeWidth="3" strokeMiterlimit="10" />
                </svg>
                <svg className="corner bottom-left" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M34 48H13.6364L2 37.7345V19" stroke="#E9F7FA" strokeWidth="3" strokeMiterlimit="10" />
                </svg>
                <svg className="corner bottom-right" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 48H36.3636L48 37.7345V19" stroke="#E9F7FA" strokeWidth="3" strokeMiterlimit="10" />
                </svg>
            </div>

            <div className="lesson-card-content">
                <div className="lesson-header">
                    <h3 className="lesson-title">{title}</h3>
                    {/* Переключатель доступа к уроку для администраторов */}
                    {adminMode && currentLessonAccess && currentLessonAccess.canModify && (
                        <div className="lesson-access-toggle" onClick={(e) => e.stopPropagation()}>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={!currentLessonAccess.isLocked}
                                    onChange={toggleLessonLock}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    )}
                </div>

                <div className="lesson-descriptions">{description}</div>

                <div className="lesson-footer">
                    <div className="button-container">
                        <button
                            className={`start-lesson-btn ${isLocked && !adminMode ? 'locked-btn' : ''}`}
                            onClick={handleStartLesson}
                            disabled={isLocked && !adminMode}
                        >
                            {adminMode ? 'ВЫПОЛНИТЬ УРОК' : getButtonText()}
                        </button>
                        <svg className="btn-corner top-left" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 1H6.8182L1 6.1328V15.5" strokeWidth="5" strokeMiterlimit="10" />
                        </svg>
                        <svg className="btn-corner top-right" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 1H18.1818L24 6.1328V15.5" strokeWidth="5" strokeMiterlimit="10" />
                        </svg>
                        <svg className="btn-corner bottom-left" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 24H6.8182L1 18.8672V9.5" strokeWidth="5" strokeMiterlimit="10" />
                        </svg>
                        <svg className="btn-corner bottom-right" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 24H18.1818L24 18.8672V9.5" strokeWidth="5" strokeMiterlimit="10" />
                        </svg>
                    </div>
                </div>
            </div>

            {isLocked && !adminMode && <div className="lesson-locked-overlay"></div>}
        </div>
    );
});

export default LessonCard; 